import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  SampleRequestCreateUpdate,
  SampleRequestDetail,
  SampleRequestFilters,
  SampleRequestsListResponse,
} from '../models/samples'
import { baseAPICall } from './baseAPICall'
import { clientInstance } from '../utils/http-client'
import type { User } from '../models/user'
import type { Product } from '../models/product'
import { downloadFile } from '../utils/csvDownloader'
import to from 'await-to-js'
import { notifyError } from './toast'

/**
 * Get list of sample requests with optional filters
 */
export async function listSampleRequests(
  filters?: SampleRequestFilters,
  signal?: AbortSignal
): Promise<SampleRequestsListResponse> {
  return baseAPICall<SampleRequestsListResponse>(() =>
    clientInstance.get('samples/sample-requests/', {
      signal,
      params: filters,
    })
  )
}

/**
 * Get a single sample request by ID
 */
export async function getSampleRequest(
  id: number,
  signal?: AbortSignal
): Promise<SampleRequestDetail> {
  return baseAPICall<SampleRequestDetail>(() =>
    clientInstance.get(`samples/sample-requests/${id}/`, { signal })
  )
}

/**
 * Create a new sample request
 */
export async function createSampleRequest(
  data: SampleRequestCreateUpdate
): Promise<SampleRequestDetail> {
  return baseAPICall<SampleRequestDetail>(() =>
    clientInstance.post('samples/sample-requests/', data)
  )
}

/**
 * Update an existing sample request
 */
export async function updateSampleRequest(
  id: number,
  data: SampleRequestCreateUpdate
): Promise<SampleRequestDetail> {
  return baseAPICall<SampleRequestDetail>(() =>
    clientInstance.put(`samples/sample-requests/${id}/`, data)
  )
}

/**
 * Cancel a sample request
 */
export async function cancelSampleRequest(
  id: number,
  cancellation_reason: string
): Promise<SampleRequestDetail> {
  return baseAPICall<SampleRequestDetail>(() =>
    clientInstance.post(`samples/sample-requests/${id}/cancel/`, {
      cancellation_reason,
    })
  )
}

/**
 * Get all users for filter options
 */
async function getUsers(): Promise<User[]> {
  return baseAPICall<User[]>(() => clientInstance.get('users/'))
}

/**
 * Hook to get users list
 */
export const useGetUsers = (disabled?: boolean) =>
  useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
    enabled: !disabled,
    refetchOnMount: false,
  })

/**
 * Hook to get user options formatted for SelectFilter
 */
export const useGetUserOptions = ({
  withNoneOption,
  onlyActive,
}: {
  withNoneOption?: boolean
  onlyActive?: boolean
} = {}) => {
  const { data: users } = useGetUsers()
  const opts = useMemo(() => {
    if (!users) return
    const out = users
      .filter((user: User) => {
        if (onlyActive) {
          return user.is_active
        }
        return true
      })
      .map((user: User) => ({
        label: `${user.first_name} ${user.last_name}`.trim(),
        value: user.id,
        original: user,
      }))

    if (withNoneOption) {
      out.unshift({ label: 'No User', value: -1, original: {} as User })
    }

    return out
  }, [users, withNoneOption, onlyActive])

  return {
    data: opts,
    isLoading: !users,
    isError: false,
  }
}

/**
 * Get all products for filter options
 */
async function getProductsOptions(withNoneOption?: boolean) {
  return baseAPICall<
    {
      label: string
      value: number
      original: Product
    }[]
  >(() =>
    clientInstance.get('products/', {
      transformResponse: (data) => {
        const response = JSON.parse(data)
        // Handle paginated response structure
        const products = response.results || response
        const productOptions: {
          label: string
          value: number
          original: Product
        }[] = products.map((product: Product) => ({
          label: product.name,
          value: product.id,
          original: product,
        }))

        if (withNoneOption) {
          productOptions.unshift({
            label: 'No Product',
            value: -1,
            original: {} as Product,
          })
        }

        return productOptions
      },
    })
  )
}

/**
 * Hook to get product options formatted for SelectFilter
 */
export const useGetProductsOptions = (withNoneOption?: boolean) =>
  useQuery({
    queryKey: ['productsOptions', withNoneOption ? 'with-none' : ''],
    queryFn: () => getProductsOptions(withNoneOption),
  })

/**
 * Export sample requests table to CSV or XLSX
 */
export async function exportSampleRequests(
  selectedColumns: string[],
  exportType: 'csv' | 'xlsx',
  filters: SampleRequestFilters,
  abortController?: AbortController
): Promise<void> {
  const [err, res] = await to(
    clientInstance.post(
      'samples/sample-requests/export/',
      {
        selected_columns: selectedColumns,
        export_type: exportType,
      },
      {
        params: filters,
        signal: abortController?.signal,
        responseType: 'blob',
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  downloadFile(
    res.data,
    `First Bite Export - Sample Requests - ${new Date().toLocaleDateString()}.${exportType}`
  )
}

// Legacy class-based API for backward compatibility
export class SamplesApi {
  static async listSampleRequests(
    filters?: SampleRequestFilters
  ): Promise<SampleRequestsListResponse> {
    return listSampleRequests(filters)
  }

  static async getSampleRequest(id: number): Promise<SampleRequestDetail> {
    return getSampleRequest(id)
  }

  static async createSampleRequest(
    data: SampleRequestCreateUpdate
  ): Promise<SampleRequestDetail> {
    return createSampleRequest(data)
  }

  static async updateSampleRequest(
    id: number,
    data: SampleRequestCreateUpdate
  ): Promise<SampleRequestDetail> {
    return updateSampleRequest(id, data)
  }

  static async cancelSampleRequest(
    id: number,
    cancellation_reason: string
  ): Promise<SampleRequestDetail> {
    return cancelSampleRequest(id, cancellation_reason)
  }
}
